export type ScheduleInterval = { start_at: string; end_at: string }
export type Schedule = Record<string, ScheduleInterval[]>
export type AgendaEvent = { start_at: string; end_at: string }

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export function getDayName(dayNumber: number): string {
  return DAY_NAMES[dayNumber]
}

export function getDayNumber(dayName: string): number {
  return DAY_NAMES.indexOf(dayName)
}

export function formatDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function parseDmy(value: string): Date | null {
  const dmy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (dmy) {
    return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]))
  }
  const ymd = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
  }
  return null
}

/** Converts "HH:mm"-bounded intervals into consecutive slot indexes of `sessionDuration` minutes. */
export function getDateTimeSlots(startTime: string, endTime: string, sessionDuration: number): number[] {
  const [startH, startM] = startTime.split(':').map(Number)
  const [endH, endM] = endTime.split(':').map(Number)
  const hourMultiplier = Math.trunc(60 / sessionDuration)

  const first = startH * hourMultiplier + Math.trunc(startM / sessionDuration)
  const minutesLastComponent = endM / sessionDuration
  const last = endH * hourMultiplier + (minutesLastComponent > Math.trunc(minutesLastComponent) ? Math.trunc(minutesLastComponent) + 1 : Math.trunc(minutesLastComponent))

  const slots: number[] = []
  for (let i = first; i <= last; i += 1) {
    slots.push(i)
  }
  slots.pop()
  return slots
}

export function groupSlots(slots: number[], duration: number, sessionDuration: number): number[][] {
  const groupSize = Math.trunc(duration / sessionDuration)
  const grouped: number[][] = []
  if (slots.length === 0) {
    return grouped
  }
  let prevSlot = slots[0]
  let currentGroup: number[] = [prevSlot]
  for (let i = 1; i < slots.length; i += 1) {
    const slot = slots[i]
    if (currentGroup.length === groupSize) {
      grouped.push(currentGroup)
      currentGroup = [slot]
    } else if (currentGroup.length < groupSize) {
      if (slot === prevSlot + 1) {
        currentGroup.push(slot)
      } else {
        currentGroup = []
      }
    }
    prevSlot = slot
  }
  if (currentGroup.length === groupSize) {
    grouped.push(currentGroup)
  }
  return grouped
}

function formatTime(hours: number, minutes: number): string {
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function eventTimeParts(value: string): { dateKey: string; time: string; weekday: number } {
  const date = new Date(value)
  return {
    dateKey: formatDateKey(date),
    time: formatTime(date.getHours(), date.getMinutes()),
    weekday: date.getDay(),
  }
}

export class AgendaProcessor {
  private slotsPerDayCache: Record<string, number[]> | null = null
  private slotsForDatesCache: Record<string, number[]> | null = null

  constructor(
    readonly schedule: Schedule,
    readonly events: AgendaEvent[],
    readonly sessionDuration: number,
  ) {}

  isEmpty(): boolean {
    return Object.keys(this.schedule).length === 0
  }

  disabledWeekDays(): number[] {
    const disabled = [0, 1, 2, 3, 4, 5, 6]
    Object.keys(this.schedule).forEach((dayName) => {
      const day = getDayNumber(dayName)
      const index = disabled.indexOf(day)
      if (index > -1 && this.schedule[dayName].length > 0) {
        disabled.splice(index, 1)
      }
    })
    return disabled
  }

  availableSlotsFor(dayName: string): number[] {
    return (this.schedule[dayName] ?? []).flatMap((interval) => getDateTimeSlots(interval.start_at, interval.end_at, this.sessionDuration))
  }

  availableSlotsPerDay(): Record<string, number[]> {
    if (!this.slotsPerDayCache) {
      this.slotsPerDayCache = Object.fromEntries(DAY_NAMES.map((dayName) => [dayName, this.availableSlotsFor(dayName)]))
    }
    return this.slotsPerDayCache
  }

  availableSlotsForSpecificDates(): Record<string, number[]> {
    if (!this.slotsForDatesCache) {
      const result: Record<string, number[]> = {}
      this.events.forEach((event) => {
        const start = eventTimeParts(event.start_at)
        const end = eventTimeParts(event.end_at)
        const eventSlots = getDateTimeSlots(start.time, end.time, this.sessionDuration)
        if (!result[start.dateKey]) {
          result[start.dateKey] = this.availableSlotsFor(getDayName(start.weekday))
        }
        result[start.dateKey] = result[start.dateKey].filter((slot) => !eventSlots.includes(slot))
      })
      this.slotsForDatesCache = result
    }
    return this.slotsForDatesCache
  }

  disabledDates(): string[] {
    const slotsForDates = this.availableSlotsForSpecificDates()
    return Object.keys(slotsForDates).filter((dateKey) => slotsForDates[dateKey].length <= 0)
  }

  hasTimesForDay(dateStr: string): boolean {
    const date = parseDmy(dateStr)
    if (!date) {
      return false
    }
    const slotsAvailable = this.availableSlotsFor(getDayName(date.getDay()))
    return slotsAvailable.length > 0 && !this.disabledDates().includes(formatDateKey(date))
  }

  slotsForDate(dateStr: string): number[] {
    const date = parseDmy(dateStr)
    if (!date) {
      return []
    }
    const dateKey = formatDateKey(date)
    const forSpecific = this.availableSlotsForSpecificDates()
    if (forSpecific[dateKey]) {
      return forSpecific[dateKey]
    }
    return this.availableSlotsPerDay()[getDayName(date.getDay())] ?? []
  }

  timeOptionsFor(dateStr: string, duration: number): string[] {
    const date = parseDmy(dateStr)
    if (!date) {
      return []
    }
    const slots = this.slotsForDate(dateStr)
    const grouped = groupSlots(slots, duration, this.sessionDuration)
    const now = new Date()
    const isToday = formatDateKey(now) === formatDateKey(date)
    const nowTime = formatTime(now.getHours(), now.getMinutes())

    const options: string[] = []
    grouped.forEach((slotGroup) => {
      const first = slotGroup[0]
      const last = slotGroup[slotGroup.length - 1]
      const startMinutes = first * this.sessionDuration
      const endMinutes = last * this.sessionDuration + this.sessionDuration
      const start = formatTime(Math.trunc(startMinutes / 60), startMinutes % 60)
      const end = formatTime(Math.trunc(endMinutes / 60), endMinutes % 60)
      if (!isToday || start >= nowTime) {
        options.push(`${start} - ${end}`)
      }
    })
    return options.sort()
  }
}

export class MultipleAgendaProcessor {
  readonly agendaProcessors: Record<string, AgendaProcessor> = {}
  readonly professionals: Record<string, { id: number; name: string }> = {}

  add(agendaProcessor: AgendaProcessor, professional: { id: number; name: string }) {
    this.agendaProcessors[String(professional.id)] = agendaProcessor
    this.professionals[String(professional.id)] = professional
  }

  isEmpty(): boolean {
    return Object.values(this.agendaProcessors).every((processor) => processor.isEmpty())
  }

  getAgenda(professionalId: string | number): AgendaProcessor | undefined {
    return this.agendaProcessors[String(professionalId)]
  }

  disabledWeekDays(professionalIds: string[] | null): number[] {
    const allDays = [0, 1, 2, 3, 4, 5, 6]
    let totalAvailable: number[] = []
    this.getSelectedKeys(professionalIds).forEach((professionalId) => {
      const disabled = this.agendaProcessors[professionalId].disabledWeekDays()
      const available = allDays.filter((day) => !disabled.includes(day))
      totalAvailable = [...new Set([...totalAvailable, ...available])]
    })
    return allDays.filter((day) => !totalAvailable.includes(day))
  }

  // A filter naming no professional of this center, such as a stale ?professional= id,
  // falls back to everyone rather than greying out the whole calendar.
  private getSelectedKeys(professionalIds: string[] | null): string[] {
    const allKeys = Object.keys(this.agendaProcessors)
    const selectedKeys = allKeys.filter((id) => professionalIds?.includes(id))
    return selectedKeys.length > 0 ? selectedKeys : allKeys
  }

  disabledDates(professionalIds: string[] | null): string[] {
    const selectedKeys = this.getSelectedKeys(professionalIds)
    const disabledCounter: Record<string, number> = {}
    selectedKeys.forEach((professionalId) => {
      this.agendaProcessors[professionalId].disabledDates().forEach((date) => {
        if (disabledCounter[date] === undefined) {
          disabledCounter[date] = 0
        }
      })
    })
    Object.keys(disabledCounter).forEach((candidateDate) => {
      selectedKeys.forEach((key) => {
        if (!this.agendaProcessors[key].hasTimesForDay(candidateDate)) {
          disabledCounter[candidateDate] += 1
        }
      })
    })
    return Object.keys(disabledCounter).filter((date) => disabledCounter[date] === selectedKeys.length)
  }

  hasTimesLeftToday(professionalIds: string[] | null): boolean {
    const today = formatDateKey(new Date())
    return this.getSelectedKeys(professionalIds).some((key) => {
      const agenda = this.agendaProcessors[key]
      return agenda.timeOptionsFor(today, agenda.sessionDuration).length > 0
    })
  }

  professionalsForDate(dateStr: string): { id: number; name: string }[] {
    return Object.keys(this.agendaProcessors)
      .filter((key) => this.agendaProcessors[key].hasTimesForDay(dateStr))
      .map((key) => this.professionals[key])
  }
}
